import { Box, CircularProgress, IconButton, Typography, useTheme } from '@mui/material';
import Divider from 'components/divider';
import KebabMenu from 'components/kebab-menu';
import ListCell from 'components/list/list-cell';
import NewListRow from 'components/new-list-row';
import Tooltip from 'components/tooltip';
import dayjs from 'dayjs';
import { CheckCircle, Eye, Pen } from 'lucide-react';
import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import titleCase from 'utils/title-case';
import ScenarioCloneDialog from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-clone-dialog';
import ScenarioDeleteDialog from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-delete-dialog';
import ScenarioListRowBody from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-list/scenario-list-row-body';
import { ScenarioListItem } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-list-item.type';
import { useScenarioStatusPolling } from 'views/bluviu-nutrition/hooks/use-scenario-status-polling';
import { BluViuNutritionParams } from 'views/bluviu-nutrition/types/bluviu-nutrition-params.type';
import { ScenarioState } from 'views/bluviu-nutrition/types/scenario-state.enum';

type Props = {
  scenario: ScenarioListItem;
};

const ScenarioListRow: React.FC<Props> = ({ scenario }) => {
  const theme = useTheme();
  const history = useHistory();
  const params = useParams<BluViuNutritionParams>();

  const { isComplete, isRunning } = useScenarioStatusPolling(scenario);

  // #region Helpers
  const getStatusColor = (state: string) => {
    switch (state) {
      case ScenarioState.CREATED:
        return theme.palette.green.o25;
      case ScenarioState.FAILED:
        return theme.palette.orange.light;
      case ScenarioState.SUBMITTED:
      case ScenarioState.SCENARIO_RESULT_SENT:
        return theme.palette.blue.o25;
      default:
        return theme.palette.blue.o25;
    }
  };

  const scenarioStatusText = (state: string) =>
    state
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .charAt(0)
      .toUpperCase() + state.replace(/([a-z])([A-Z])/g, '$1 $2').slice(1);

  // #endregion

  const hasResults = scenario.state === ScenarioState.SCENARIO_RESULT_SENT;
  const actionTab = hasResults ? 'results' : 'setup';
  const ActionIcon = hasResults ? Eye : Pen;

  return (
    <>
      <NewListRow
        colSpan={8}
        body={
          scenario.appliedAs ? (
            <>
              <Divider sx={{ borderColor: theme.palette.darkBlue.o25 }} />
              <ScenarioListRowBody scenario={scenario} />
            </>
          ) : undefined
        }>
        <ListCell>
          <Box display={'flex'} flexDirection={'row'} gap={4}>
            {isComplete ? (
              <CheckCircle size={18} style={{ alignSelf: 'center' }} />
            ) : isRunning ? (
              <CircularProgress size={18} style={{ alignSelf: 'center' }} />
            ) : null}
            {scenario.name}
          </Box>
        </ListCell>
        <ListCell>{scenario.generation.displayName}</ListCell>
        <ListCell>{scenario.scenarioStart ? dayjs(scenario.scenarioStart).format('YYYY-MM-DD') : '-'}</ListCell>
        <ListCell>{scenario.lastUpdated ? dayjs(scenario.lastUpdated).format('YYYY-MM-DD') : '-'}</ListCell>
        <ListCell sx={{ textAlign: 'left' }}>
          <Box display="flex" justifyContent="flex-start" alignItems="center" gap={1}>
            <Typography
              sx={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                padding: '4px 15px',
                backgroundColor: getStatusColor(scenario.state),
                borderRadius: '15px',
              }}>
              {scenarioStatusText(scenario.state)}
            </Typography>
          </Box>
        </ListCell>
        <ListCell sx={{ textAlign: 'left' }}>
          {scenario.effectiveTo == null && scenario.appliedAs ? titleCase(scenario.appliedAs ?? '') : '-'}
        </ListCell>
        <ListCell align="right">
          <Box display="flex" justifyContent="flex-end" alignItems="center" flexDirection={'row'} gap={2}>
            <Tooltip title={hasResults ? 'View' : 'Edit'}>
              <IconButton
                onClick={() => history.push(`/bluviu-nutrition/r2/${params.globalSiteId}/${scenario.id}/${actionTab}`)}>
                <ActionIcon size={22} />
              </IconButton>
            </Tooltip>
            <KebabMenu dark>
              <ScenarioCloneDialog scenario={scenario} />
              <ScenarioDeleteDialog scenario={scenario} />
            </KebabMenu>
          </Box>
        </ListCell>
        {!scenario.appliedAs && <ListCell />}
      </NewListRow>
    </>
  );
};

export default React.memo(ScenarioListRow);
